import { arrayOf, bool, number, string } from 'prop-types';
import React from 'react';

import { allStatusesEnum } from '../../balance/constants';
import Tabs from '../../generic/Tabs';
import Titled from '../../layout/Titled';
import { invoiceStatusCountShape } from '../../prop-types';
import api from '../../REST/api';
import withResolved from '../../REST/with-resolved';
import AsyncList from '../AsyncList';
import dump from '../dump';
import {
  exportedInvoiceColumns,
  invoiceColumns,
  requestedChanges,
} from '../invoice-columns';

class ListCategorizedInvoices extends React.PureComponent {
  getCount(status) {
    const { counts } = this.props;
    const countObject = counts.find(count => count.status === status);
    return countObject ? countObject.count : 0;
  }

  getList(status, columns, allowUIDownload = true) {
    const {
      ownerId,
      salesTeam,
      salesChannel,
      merchantIds,
      allowSelection,
      ...rest
    } = this.props;
    const downloadProps = allowUIDownload
      ? { dump }
      : { displayExportButton: true };
    return (
      <AsyncList
        columns={columns}
        allColumns={columns}
        allowSelection={
          allowSelection && status === allStatusesEnum.APPROVED.value
        }
        storedName="INVOICE_FILTERS"
        searchParams={{
          status,
          ownerId,
          salesTeam,
          salesChannel,
          merchantIds,
        }}
        {...rest}
        {...downloadProps}
      />
    );
  }

  statusLabel(status) {
    return `${status.label} (${this.getCount(status.value)})`;
  }

  filterHiddenColumns(columns) {
    const { hiddenColumns } = this.props;
    return columns.filter(col => !hiddenColumns.includes(col.Header));
  }

  tabs() {
    const { counts, showOnlyAccountingStatuses } = this.props;

    const allTab = {
      label: `ALL (${counts
        .map(c => c.count)
        .reduce((acc, count) => acc + count, 0)})`,
      value: 'all',
      content: this.getList(
        [], // No status needs to be passed in query in 'All' tab
        this.filterHiddenColumns([
          ...invoiceColumns,
          ...exportedInvoiceColumns,
        ]),
        false,
      ),
    };

    const draftTab = {
      label: this.statusLabel(allStatusesEnum.DRAFT),
      value: allStatusesEnum.DRAFT.value,
      content: this.getList(
        allStatusesEnum.DRAFT.value,
        this.filterHiddenColumns([
          ...invoiceColumns,
          ...exportedInvoiceColumns,
        ]),
      ),
    };
    const inReviewTab = {
      label: this.statusLabel(allStatusesEnum.IN_REVIEW),
      value: allStatusesEnum.IN_REVIEW.value,
      content: this.getList(
        allStatusesEnum.IN_REVIEW.value,
        this.filterHiddenColumns([
          ...invoiceColumns,
          ...exportedInvoiceColumns,
        ]),
      ),
    };

    const changesRequestedTab = {
      label: this.statusLabel(allStatusesEnum.CHANGES_REQUESTED),
      value: allStatusesEnum.CHANGES_REQUESTED.value,
      content: this.getList(
        allStatusesEnum.CHANGES_REQUESTED.value,
        this.filterHiddenColumns([
          ...invoiceColumns,
          ...exportedInvoiceColumns,
          requestedChanges,
        ]),
      ),
    };
    const approvedTab = {
      label: this.statusLabel(allStatusesEnum.APPROVED),
      value: allStatusesEnum.APPROVED.value,
      content: this.getList(
        allStatusesEnum.APPROVED.value,
        this.filterHiddenColumns([
          ...invoiceColumns,
          ...exportedInvoiceColumns,
        ]),
      ),
    };
    const exportedTab = {
      label: this.statusLabel(allStatusesEnum.EXPORTED),
      value: allStatusesEnum.EXPORTED.value,
      content: this.getList(
        allStatusesEnum.EXPORTED.value,
        this.filterHiddenColumns([
          ...invoiceColumns,
          ...exportedInvoiceColumns,
        ]),
      ),
    };
    const canceledTab = {
      label: this.statusLabel(allStatusesEnum.CANCELED),
      value: allStatusesEnum.CANCELED.value,
      content: this.getList(
        allStatusesEnum.CANCELED.value,
        this.filterHiddenColumns([
          ...invoiceColumns,
          ...exportedInvoiceColumns,
        ]),
      ),
    };
    const result = [];

    if (!showOnlyAccountingStatuses) {
      result.push(allTab, draftTab, inReviewTab);
    }
    result.push(changesRequestedTab, approvedTab, exportedTab, canceledTab);

    return result;
  }

  render() {
    const { title, titleSize, showOnlyAccountingStatuses } = this.props;

    return (
      <Titled title={title} size={titleSize}>
        <Tabs
          tabs={this.tabs()}
          defaultActive={
            showOnlyAccountingStatuses
              ? allStatusesEnum.CHANGES_REQUESTED.label
              : allStatusesEnum.DRAFT.label
          }
          id="categorizedInvoices"
        />
      </Titled>
    );
  }
}

ListCategorizedInvoices.propTypes = {
  title: string,
  titleSize: string,
  hiddenColumns: arrayOf(string.isRequired),
  counts: arrayOf(invoiceStatusCountShape.isRequired).isRequired,
  ownerId: number,
  salesTeam: string,
  salesChannel: string,
  showOnlyAccountingStatuses: bool,
  merchantIds: arrayOf(string.isRequired),
  allowSelection: bool,
};

ListCategorizedInvoices.defaultProps = {
  title: 'Invoices',
  titleSize: undefined,
  hiddenColumns: [],
  ownerId: undefined,
  salesTeam: undefined,
  salesChannel: undefined,
  showOnlyAccountingStatuses: false,
  merchantIds: [],
  allowSelection: false,
};

export default withResolved({
  query: api.getInvoicesCount,
  deriveQueryArgs: ({ ownerId, salesTeam, salesChannel, merchantIds }) => ({
    search: {
      ownerId,
      salesTeam,
      salesChannel,
      merchantIds,
    },
  }),
  as: 'counts',
})(ListCategorizedInvoices);
